import { DomHelpers } from '../../core/DomHelpers.js';
import { hasMainValue } from '../../core/EncodeValues.js';

// src/view/controllers/ViewValidationController.ts
/**
 * Валидация требований на кнопках (data-required, data-valid).
 */
class ViewValidationController {
    constructor(view) {
        this.view = view;
    }
    /**
     * Проверка полноты заполнения формы согласно атрибутам на кнопке.
     * @returns true — если можно выполнять действие; false — если есть блокирующие нарушения.
     */
    checkButtonRequires(btn) {
        let valid = true;
        // --- 1) data-required ---
        const reqStr = btn.getAttribute("data-required");
        let commonPlaces = []; // из опции '*'
        if (reqStr) {
            const tokens = reqStr.split(",").map(s => s.trim()).filter(Boolean);
            for (const token of tokens) {
                const parts = token.split(":").map(s => s.trim()).filter(Boolean);
                const keyOrMarker = parts[0];
                const item = this.view.dom.itemByKey(keyOrMarker);
                if (!item)
                    continue;
                let itemValid = true;
                const has = hasMainValue(item);
                // трактуем undefined как "не блокируем"
                const ok = has === undefined ? true : !!has;
                if (!ok) {
                    // 2026-08-31: класс input-validation-error общий для всех валидаторов,
                    // поэтому свою метку помечаем отдельным признаком — без него на
                    // следующем клике её не отличить от ошибки чужого валидатора.
                    itemValid = false;
                    item.dataset.requiredError = "1";
                    DomHelpers.addClass(item, "input-validation-error");
                    this.clearRequiredOnInput(item);
                }
                else {
                    // Поле заполнено — снимаем только собственную метку. Раньше здесь
                    // было единое «помечено ошибкой — оставляем как есть», и поле без
                    // своего валидатора (обычный textbox без data-regex) оставалось
                    // помеченным навсегда: кнопка молча не работала до перезагрузки.
                    this.clearRequiredError(item);
                    // Чужие ошибки (в том числе у потомков) по-прежнему блокируют.
                    if (this.hasValidationError(item))
                        itemValid = false;
                }
                // обновляем общий флаг
                if (!itemValid)
                    valid = false;
                // Обработка меток/лейблов после key:place1:*:place2...
                for (let i = 1; i < parts.length; i++) {
                    const placeKey = parts[i];
                    if (placeKey === "*") {
                        // собираем общие плейсхолдеры для показа/скрытия после цикла
                        if (i > 2)
                            commonPlaces.push(parts[i - 1]);
                        continue;
                    }
                    this.view.dom.showPlace(placeKey, !itemValid ? true : false);
                }
            }
            // Общие плейсхолдеры ('*') — показываем/скрываем одним махом
            if (commonPlaces.length) {
                for (const pk of commonPlaces) {
                    this.view.dom.showPlace(pk, !valid);
                }
            }
        }
        // --- 2) data-valid ---
        let noErr = true;
        commonPlaces = [];
        const valStr = btn.getAttribute("data-valid");
        if (valStr) {
            const tokens = valStr.split(",").map(s => s.trim()).filter(Boolean);
            for (const token of tokens) {
                const parts = token.split(":").map(s => s.trim()).filter(Boolean);
                const key = parts[0];
                const item = this.view.dom.itemByKey(key);
                // «Есть ли ошибки у поля» 
                const itemHasErr = this.hasValidationError(item);
                if (itemHasErr)
                    noErr = false;
                // Управление плейсхолдерами для каждого поля
                for (let i = 1; i < parts.length; i++) {
                    const placeKey = parts[i];
                    if (placeKey === "*") {
                        if (i > 2)
                            commonPlaces.push(parts[i - 1]);
                        continue;
                    }
                    this.view.dom.showPlace(placeKey, itemHasErr);
                }
            }
            // Общие плейсхолдеры ('*') — отражают факт наличия ошибок
            if (commonPlaces.length) {
                for (const pk of commonPlaces) {
                    this.view.dom.showPlace(pk, !noErr);
                }
            }
        }
        return valid && noErr;
    }
    /** Снять метку незаполненного обязательного поля, если она стоит. */
    clearRequiredError(item) {
        if (!item.dataset.requiredError)
            return;
        delete item.dataset.requiredError;
        DomHelpers.removeClass(item, "input-validation-error");
    }
    /**
     * Снять метку сразу, как полю вернули значение, а не на следующем клике.
     *
     * Validation.init сюда не помогает: он слушает input и change, но вешает
     * обработчики только на поля с data-regex или с виджетным validate(), а у
     * required-поля своих валидаторов может не быть вовсе. Подписка одноразовая
     * и живёт только пока висит метка.
     *
     * Слушаем оба события: input даёт реакцию прямо по ходу набора, но его не
     * шлёт select и часть виджетов — там срабатывает change. Оба всплывают,
     * поэтому подписка на контейнер [data-key] ловит и вложенный контрол.
     */
    clearRequiredOnInput(item) {
        if (item.dataset.requiredWatch)
            return;
        item.dataset.requiredWatch = "1";
        const off = () => {
            item.removeEventListener("input", handler);
            item.removeEventListener("change", handler);
            delete item.dataset.requiredWatch;
        };
        const handler = () => {
            // Метку могли снять без нас — тогда следить больше не за чем.
            if (!item.dataset.requiredError) {
                off();
                return;
            }
            // undefined — проверка неприменима, трактуем как заполненное
            // (так же, как в checkButtonRequires).
            if (hasMainValue(item) === false)
                return;
            this.clearRequiredError(item);
            off();
        };
        item.addEventListener("input", handler);
        item.addEventListener("change", handler);
    }
    /** Проверка: есть ли ошибки у элемента или его потомков. */
    hasValidationError(item) {
        if (!item)
            return false;
        return (item.classList.contains("err") ||
            item.classList.contains("input-validation-error") ||
            !!item.querySelector(".err, .input-validation-error"));
    }
}

export { ViewValidationController };
